/**
 * BrowseNumbers — Browse & add numbers to cart
 *
 * Fixes in this version:
 *  1. Docs column → click opens a modal with the required documents list
 *  2. Cart buttons → "Add to Cart" / "Remove from Cart" per row (no more Select modal here)
 *  3. Floating cart badge → links to /did/number-cart
 *  4. Single loading indicator (table-level only; no city-dropdown spinner visible on first load)
 *  5. Filter bar → "Clear all" on the LEFT; no individual ✕ on inputs
 *  6. Monthly shows ourMonthlyPrice; Activation shows ourActivationPrice; Per Min shows ourPerMinute
 */

import React, { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Tag, Select, Input, Spin, Empty, Tooltip, Modal,
} from "antd";
import ReactCountryFlag from "react-country-flag";
import {
  searchInventoryNumbers,
  fetchInventoryCountries,
  fetchCities,
  fetchNumbersByCity,
  setSelectedCountry,
  clearInventory,
  loadFromCache,
} from "../../../core/data/redux/slices/numberInventorySlice";
import { getDialCode } from "../../../core/utils/dialCodes";
import {
  fetchCart,
  addToCart,
  removeFromCart,
} from "../../../core/data/redux/slices/numberCartSlice";
import { all_routes } from "../../router/all_routes";

// ── Helpers ───────────────────────────────────────────────────────────────────

const Flag = ({ code, size = 28, style = {} }) =>
  code ? (
    <ReactCountryFlag
      countryCode={code}
      svg
      style={{ width: size, height: size * 0.75, borderRadius: 3, objectFit: "cover", ...style }}
      title={code}
    />
  ) : (
    <span style={{ fontSize: size * 0.8, lineHeight: 1 }}>🌐</span>
  );

const { Option } = Select;
const route = all_routes;

// ── Document label helper ─────────────────────────────────────────────────────
// type 1 → "Personal ID"
// type 2 → "Proof of address (any location)"
// type 3 → "Proof of local address ({countryName})"
// fallback → use the name string from the API
export function getDocLabel(doc, countryName = "") {
  if (!doc) return "";
  if (doc.type === 1) return "Personal ID";
  if (doc.type === 2) return "Proof of address (any location)";
  if (doc.type === 3) return `Proof of local address${countryName ? ` (${countryName})` : ""}`;
  return doc.name || `Document #${doc.type}`;
}

// Build display list from required_documents.
// Rule: if type 3 (Proof of LOCAL address) is present, suppress type 2
//       (Proof of address) — local proof already satisfies the general requirement.
// If the array is empty, no docs are needed — do NOT inject Personal ID.
export function buildDocList(required_documents = [], countryName = "") {
  const docs = required_documents || [];
  const hasLocalProof = docs.some((d) => d.type === 3);
  return docs
    .filter((d) => !(hasLocalProof && d.type === 2))   // drop generic proof when local proof exists
    .map((d) => ({ ...d, label: getDocLabel(d, countryName) }));
}

// ── Breadcrumb ────────────────────────────────────────────────────────────────
function Breadcrumb({ steps, onClickIndex, countryCode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20, fontSize: 13, flexWrap: "wrap" }}>
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          {i > 0 && <i className="ti ti-chevron-right" style={{ fontSize: 11, color: "#bfbfbf" }} />}
          <span
            onClick={() => i < steps.length - 1 && onClickIndex(i)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              color:      i === steps.length - 1 ? "#1a1a2e" : "#667eea",
              fontWeight: i === steps.length - 1 ? 600 : 400,
              cursor:     i < steps.length - 1 ? "pointer" : "default",
            }}
          >
            {i === 1 && countryCode && <Flag code={countryCode} size={16} style={{ borderRadius: 2 }} />}
            {s}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Required Documents Modal
// Shows smart labels + always includes Personal ID as mandatory
// ─────────────────────────────────────────────────────────────────────────────
const DocsModal = ({ open, docs, number, countryName, onClose }) => {
  const fullList = buildDocList(docs || [], countryName);
  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose} style={{ borderRadius: 8 }}>Close</Button>}
      width={460}
      destroyOnClose
    >
      <div style={{
        background: "linear-gradient(135deg, #3C2371, #8e77fa)",
        borderRadius: "8px 8px 0 0", padding: "18px 24px",
        margin: "-20px -24px 20px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <i className="ti ti-file-certificate" style={{ marginRight: 8 }} />Required Documents
        </h5>
        {number && <p style={{ color: "rgba(255,255,255,0.7)", margin: "4px 0 0", fontSize: 12 }}>To purchase +{number}</p>}
      </div>

      {fullList.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px 0", color: "#52c41a" }}>
          <i className="ti ti-circle-check" style={{ fontSize: 36, display: "block", marginBottom: 8 }} />
          <p style={{ fontWeight: 600, margin: 0 }}>No documents required</p>
          <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>This number can be purchased without uploading any documents.</p>
        </div>
      ) : (
        <>
          <p style={{ color: "#595959", fontSize: 13, marginBottom: 14 }}>
            The following documents must be submitted in <strong>KYC Documents</strong> before this number can be activated:
          </p>
          {fullList.map((d, i) => (
            <div key={d.type ?? i} style={{
              display: "flex", alignItems: "flex-start", gap: 12,
              padding: "10px 14px", marginBottom: 8,
              background: "#f8f9ff", borderRadius: 8, border: "1px solid #e8edff",
            }}>
              <div style={{
                background: "linear-gradient(135deg,#3C2371,#8e77fa)",
                color: "#fff", borderRadius: "50%",
                width: 26, height: 26, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>{d.label}</div>
                {d.type === 1 && <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>Examples: Passport, national ID card, driver's license</div>}
                {d.type === 2 && <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>Examples: Utility bill, bank statement (within 90 days)</div>}
                {d.type === 3 && <div style={{ fontSize: 11, color: "#fa8c16", marginTop: 2 }}>Must be issued in {countryName || "the local country"}</div>}
              </div>
            </div>
          ))}
          <div style={{
            background: "#fffbe6", border: "1px solid #ffe58f",
            borderRadius: 8, padding: "10px 14px", marginTop: 8,
            fontSize: 12, color: "#613400",
          }}>
            <i className="ti ti-info-circle" style={{ marginRight: 6 }} />
            Upload in <strong>KYC Documents → Upload Document</strong> before purchasing.
          </div>
        </>
      )}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 — Country grid
// ─────────────────────────────────────────────────────────────────────────────
const CountryStep = ({ countries, loading, onSelect, cartItems, onGoToCart }) => {
  const [search, setSearch] = useState("");
  const filtered = countries.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <div style={{ textAlign: "center" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#8c8c8c" }}>Loading countries…</p>
        </div>
      </div>
    );
  }

  if (!countries.length) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <i className="ti ti-world-off" style={{ fontSize: 48, color: "#bfbfbf", display: "block", marginBottom: 16 }} />
        <p style={{ color: "#8c8c8c", fontSize: 15 }}>No countries available.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        background: "linear-gradient(135deg, #3C2371 0%, #5B6ABF 55%, #8e77fa 100%)",
        borderRadius: 16, padding: "32px 32px 28px", marginBottom: 28, color: "#fff",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
          <h3 style={{ color: "#fff", fontWeight: 700, fontSize: 22, margin: 0 }}>
            <i className="ti ti-world" style={{ marginRight: 10 }} />Select a Country
          </h3>
          <button
            onClick={onGoToCart}
            style={{
              background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.4)",
              borderRadius: 10, padding: "8px 18px", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
          >
            <i className="ti ti-shopping-cart" />
            View Cart
            {cartItems?.length > 0 && (
              <span style={{
                background: "#fff", color: "#3C2371",
                borderRadius: "50%", width: 20, height: 20,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>
                {cartItems.length}
              </span>
            )}
          </button>
        </div>
        <p style={{ color: "rgba(255,255,255,0.75)", marginBottom: 20, fontSize: 14 }}>
          {countries.length} countries with available numbers — click to browse instantly
        </p>
        <Select
          showSearch
          placeholder="🔍  Search or select a country..."
          style={{ width: "100%", maxWidth: 500 }}
          size="large"
          filterOption={(input, option) =>
            option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
          }
          onSelect={(id) => {
            const c = countries.find((x) => x.id === id);
            if (c) onSelect(c);
          }}
          optionLabelProp="label"
          dropdownStyle={{ maxHeight: 400 }}
        >
          {countries.map((c) => (
            <Option key={c.id} value={c.id} label={c.name} searchlabel={c.name}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                <Flag code={c.short_name} size={22} />
                <span style={{ fontWeight: 500 }}>{c.name}</span>
              </div>
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
        {filtered.map((c) => (
          <div
            key={c.id}
            onClick={() => onSelect(c)}
            style={{
              background: "#fff", border: "1.5px solid #e8e8e8",
              borderRadius: 12, padding: "16px 10px",
              textAlign: "center", cursor: "pointer",
              transition: "all 0.18s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#5B6ABF";
              e.currentTarget.style.boxShadow   = "0 6px 20px rgba(91,106,191,0.15)";
              e.currentTarget.style.transform   = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e8e8e8";
              e.currentTarget.style.boxShadow   = "none";
              e.currentTarget.style.transform   = "none";
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
              <Flag code={c.short_name} size={38} style={{ borderRadius: 4 }} />
            </div>
            <div style={{ fontWeight: 600, fontSize: 11, color: "#1a1a2e", lineHeight: 1.4 }}>{c.name}</div>
            {getDialCode(c.short_name) && (
              <div style={{ fontSize: 10, color: "#667eea", marginTop: 2, fontWeight: 600 }}>
                {getDialCode(c.short_name)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — Search Results with Cart buttons
// ─────────────────────────────────────────────────────────────────────────────
const SearchResultsStep = ({
  country,
  numbers,
  total,
  loading,
  page,
  cityOptions,
  citiesLoading,
  filters,       // { number_contains, city_name_contains } — city_name_contains holds the display name
  activeCityId,  // numeric city ID currently selected (for the Select's controlled value)
  onFilterChange,
  onCitySelect,  // (cityId: number|null, cityName: string) => void
  onPageChange,
  cartItems,
  cartAdding,
  cartRemoving,
  onAddToCart,
  onRemoveFromCart,
  onGoToCart,
  // Change country
  countries,
  onCountryChange,
}) => {
  const [changeCountryOpen, setChangeCountryOpen] = useState(false);
  // Local state for the docs modal
  const [docsModal, setDocsModal] = useState({ open: false, docs: [], number: "", countryName: "" });

  // Set of did_ids currently in cart (for instant UI feedback)
  const cartSet = new Set((cartItems || []).map((i) => i.did_id));

  const columns = [
    {
      title: "Phone Number",
      dataIndex: "number",
      key: "number",
      render: (v) => (
        <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, color: "#1a1a2e", letterSpacing: "0.02em" }}>
          +{v}
        </span>
      ),
    },
    {
      title: "City / Area",
      dataIndex: "city",
      key: "city",
      render: (v) => <span style={{ color: "#595959", fontSize: 13 }}>{v || "—"}</span>,
    },
    {
      title: "Channels",
      dataIndex: "channels",
      key: "channels",
      align: "center",
      render: (v) => <span style={{ fontWeight: 600, color: "#3C2371" }}>{v ?? "—"}</span>,
    },
    {
      title: "Activation",
      key: "activation",
      align: "right",
      render: (_, r) => {
        const our = Number(r.ourActivationPrice ?? r.activation ?? 0);
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700, color: "#1a1a2e", fontSize: 13 }}>${our.toFixed(2)}</div>
          </div>
        );
      },
    },
    {
      title: "Monthly",
      key: "monthly",
      align: "right",
      render: (_, r) => {
        const our = Number(r.ourMonthlyPrice ?? r.monthly_fee ?? r.monthly ?? 0);
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700, color: "#52c41a", fontSize: 14 }}>
              ${our.toFixed(2)}<span style={{ color: "#8c8c8c", fontWeight: 400, fontSize: 11 }}>/mo</span>
            </div>
          </div>
        );
      },
    },
    // {
    //   title: "Per Min",
    //   key: "per_minute",
    //   align: "right",
    //   render: (_, r) => {
    //     const our = Number(r.ourPerMinute ?? r.per_minute ?? 0);
    //     return (
    //       <div style={{ textAlign: "right" }}>
    //         <span style={{ fontSize: 12, color: "#8c8c8c" }}>
    //           {our > 0 ? `$${our.toFixed(3)}` : "—"}
    //         </span>
    //       </div>
    //     );
    //   },
    // },
    {
      title: (
        <Tooltip title="Rate charged to your account per minute for outbound calls to this number type">
          <span>Call Rate <i className="ti ti-info-circle" style={{ fontSize: 11, color: "#8c8c8c" }} /></span>
        </Tooltip>
      ),
      key: "outbound_rate",
      align: "right",
      render: (_, r) => {
        const rate = r.outboundRate;
        return (
          <div style={{ textAlign: "right" }}>
            <span style={{ fontSize: 12, color: rate != null ? "#1a1a2e" : "#bfbfbf", fontWeight: rate != null ? 600 : 400 }}>
              {rate != null ? `$${Number(rate).toFixed(4)}` : "—"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Documents",
      key: "docs",
      align: "center",
      render: (_, r) => {
        const docs     = r.required_documents || [];
        const fullList = buildDocList(docs, r.country || country?.name || "");
        if (!fullList.length) {
          return (
            <Tag color="green" style={{ borderRadius: 20, fontSize: 10 }}>
              <i className="ti ti-circle-check" style={{ marginRight: 3 }} />None
            </Tag>
          );
        }
        return (
          <Tooltip title="Click to view required documents">
            <Tag
              color="orange"
              style={{ borderRadius: 20, fontSize: 10, cursor: "pointer" }}
              onClick={() => setDocsModal({ open: true, docs, number: r.number, countryName: r.country || country?.name || "" })}
            >
              <i className="ti ti-file-certificate" style={{ marginRight: 3 }} />
              {fullList.length} required
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 150,
      render: (_, r) => {
        const inCart  = cartSet.has(r.id || r.did_id);
        const isAdding   = cartAdding  === (r.id || r.did_id);
        const isRemoving = cartRemoving === (r.id || r.did_id);

        if (inCart) {
          return (
            <Button
              size="small"
              loading={isRemoving}
              onClick={() => onRemoveFromCart(r.id || r.did_id)}
              style={{
                borderRadius: 8, border: "1.5px solid #ff4d4f",
                color: "#ff4d4f", background: "#fff2f0",
                fontWeight: 600, width: "100%",
              }}
            >
              {!isRemoving && <i className="ti ti-trash" style={{ marginRight: 4 }} />}
              Remove
            </Button>
          );
        }
        return (
          <Button
            size="small"
            loading={isAdding}
            onClick={() => onAddToCart(r)}
            style={{
              borderRadius: 8,
              background: "linear-gradient(135deg, #3C2371, #8e77fa)",
              border: "none", color: "#fff",
              fontWeight: 600, width: "100%",
            }}
          >
            {!isAdding && <i className="ti ti-shopping-cart-plus" style={{ marginRight: 4 }} />}
            Add to Cart
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      {/* Docs modal */}
      <DocsModal
        open={docsModal.open}
        docs={docsModal.docs}
        number={docsModal.number}
        countryName={docsModal.countryName}
        onClose={() => setDocsModal({ open: false, docs: [], number: "", countryName: "" })}
      />

      {/* Change Country Modal */}
      <Modal
        title={null}
        open={changeCountryOpen}
        onCancel={() => setChangeCountryOpen(false)}
        footer={null}
        width={460}
        destroyOnClose
      >
        <div style={{
          background: "linear-gradient(135deg,#3C2371,#8e77fa)",
          borderRadius: "8px 8px 0 0", padding: "18px 24px",
          margin: "-20px -24px 20px", color: "#fff",
        }}>
          <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
            <i className="ti ti-world" style={{ marginRight: 8 }} />Change Country
          </h5>
        </div>
        <Select
          showSearch
          autoFocus
          placeholder="Search country…"
          style={{ width: "100%" }}
          size="large"
          optionLabelProp="label"
          filterOption={(input, option) =>
            option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
          }
          onSelect={(id) => {
            const c = countries.find((x) => x.id === id);
            if (c) { onCountryChange(c); setChangeCountryOpen(false); }
          }}
        >
          {(countries || []).map((c) => {
            const dial = getDialCode(c.short_name);
            return (
              <Option
                key={c.id}
                value={c.id}
                label={`${c.name}${dial ? ` (${dial})` : ""}`}
                searchlabel={`${c.name} ${c.short_name} ${dial}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                  <Flag code={c.short_name} size={22} />
                  <span style={{ fontWeight: 500 }}>{c.name}</span>
                  {dial && <span style={{ color: "#667eea", fontSize: 12, marginLeft: "auto" }}>{dial}</span>}
                </div>
              </Option>
            );
          })}
        </Select>
      </Modal>

      {/* Country header + cart button */}
      <div style={{
        background: "linear-gradient(135deg, #3C2371 0%, #5B6ABF 55%, #8e77fa 100%)",
        borderRadius: 16, padding: "22px 28px", marginBottom: 20, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Flag code={country.short_name} size={52} style={{ borderRadius: 6 }} />
          <div>
            <h3 style={{ color: "#fff", fontWeight: 700, margin: 0, fontSize: 20 }}>
              {country.name}
              {getDialCode(country.short_name) && (
                <span style={{ fontSize: 15, fontWeight: 400, opacity: 0.8, marginLeft: 8 }}>
                  ({getDialCode(country.short_name)})
                </span>
              )}
            </h3>
            <p style={{ color: "rgba(255,255,255,0.75)", margin: "4px 0 0", fontSize: 13 }}>
              {loading ? "Searching numbers…" : `${total} available number${total !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>

        {/* Right-side action buttons */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Change country */}
          <button
            onClick={() => setChangeCountryOpen(true)}
            style={{
              background: "rgba(255,255,255,0.12)", border: "1.5px solid rgba(255,255,255,0.3)",
              borderRadius: 10, padding: "8px 16px", color: "#fff",
              fontWeight: 600, fontSize: 12, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
          >
            <i className="ti ti-world" />Change Country
          </button>

          {/* View Cart */}
          <button
            onClick={onGoToCart}
            style={{
              background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.4)",
              borderRadius: 10, padding: "8px 18px", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
          >
            <i className="ti ti-shopping-cart" />
            View Cart
            {(cartItems?.length > 0) && (
              <span style={{
                background: "#fff", color: "#3C2371",
                borderRadius: "50%", width: 20, height: 20,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>
                {cartItems.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        display: "flex", gap: 10, marginBottom: 16,
        flexWrap: "wrap", alignItems: "center",
        background: "#fff", borderRadius: 10, padding: "14px 16px",
        border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        {/* Number search */}
        <Input
          prefix={<i className="ti ti-search" style={{ color: "#bfbfbf" }} />}
          placeholder="Search by phone number…"
          value={filters.number_contains}
          onChange={(e) => onFilterChange("number_contains", e.target.value)}
          style={{ maxWidth: 240 }}
        />

        {/* City filter — value is the numeric city ID (string) so selection is exact,
            no accent/substring false-positives. Triggers GET /cities/:city_id/dids */}
        <Select
          allowClear
          showSearch
          placeholder="Filter by city / area code…"
          value={activeCityId ? String(activeCityId) : undefined}
          onChange={(v) => {
            if (v) {
              const cityObj = cityOptions.find((c) => String(c.id) === v);
              onCitySelect(cityObj?.id ?? Number(v), cityObj?.name ?? v);
            } else {
              onCitySelect(null, "");
            }
          }}
          style={{ minWidth: 230 }}
          filterOption={(input, option) =>
            option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
          }
          optionLabelProp="label"
          notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={citiesLoading ? "Loading cities…" : "No cities"} />}
        >
          {cityOptions.map((c) => {
            const label = `${c.name} (Starts with ${c.area_code})`;
            return (
              <Option key={c.id} value={String(c.id)} label={label} searchlabel={`${c.name} ${c.area_code}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>
                    <span style={{ fontWeight: 600, color: "#1a1a2e", marginRight: 4 }}>{c.name}</span>
                    <span style={{ fontSize: 11, color: "#8c8c8c" }}>(Starts with {c.area_code})</span>
                  </span>
                  <span style={{ background: "#f0f4ff", color: "#5B6ABF", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 600, marginLeft: 8 }}>
                    {c.count ?? 0}
                  </span>
                </div>
              </Option>
            );
          })}
        </Select>

        {/* Clear all — RIGHT side, only shown when a filter is active */}
        {(filters.number_contains || filters.city_name_contains) && (
          <Button
            size="small"
            style={{ borderRadius: 6, marginLeft: "auto" }}
            onClick={() => {
              onFilterChange("number_contains", "");
              onFilterChange("city_name_contains", "");
            }}
          >
            <i className="ti ti-x" style={{ marginRight: 4 }} />Clear filters
          </Button>
        )}
      </div>

      {/* Results table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", overflow: "hidden" }}>
        <Table
          dataSource={numbers}
          columns={columns}
          rowKey={(r) => r.id || r.number}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            total,
            pageSize: 25,
            current: page,
            onChange: (p) => {
              onPageChange(p);
              window.scrollTo({ top: 0, behavior: "smooth" });
            },
            showTotal: (t, [from, to]) => `${from}–${to} of ${t} numbers`,
            showSizeChanger: false,
            style: { padding: "12px 16px" },
          }}
          locale={{
            // Do NOT render a custom emptyText while loading — the Table's own
            // loading overlay already shows a spinner; a second one appears because
            // Ant Design v5 renders emptyText even when loading=true and data=[].
            emptyText: loading ? null : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  filters.number_contains || filters.city_name_contains
                    ? "No numbers match your filters — try clearing them"
                    : "No numbers available"
                }
              />
            ),
          }}
        />
      </div>

      <style>{`
        .ant-table-tbody > tr:nth-child(even) > td { background: #fafbff; }
        .ant-table-tbody > tr:hover > td { background: #f0f4ff !important; }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main BrowseNumbers Component
// ─────────────────────────────────────────────────────────────────────────────
const BrowseNumbers = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const {
    countries, countriesLoading,
    cities,    citiesLoading,
    numbers,   numbersTotal, numbersLoading,
    selectedCountry,
  } = useSelector((s) => s.numberInventory);

  const { items: cartItems, adding: cartAdding, removing: cartRemoving } =
    useSelector((s) => s.numberCart);

  const [step, setStep]             = useState(1);
  const [page, setPage]             = useState(1);
  const [filters, setFilters]       = useState({ number_contains: "", city_name_contains: "" });
  // When a city is selected from the dropdown we use fetchNumbersByCity (exact city ID)
  // instead of searchInventoryNumbers to avoid accent/substring false-positives
  // (e.g. "Reykjavik" matching both "Reykjavik" and "Reykjavík")
  const [activeCityId, setActiveCityId] = useState(null);

  const debounceRef = useRef(null);
  // Session-level cache: key = "countryId_cityId_page" → API payload
  // Avoids re-fetching the same city on every selection change
  const cityCache   = useRef({});

  useEffect(() => {
    // Reset any previous country selection so navigating away and back
    // always starts at the country grid (no stale breadcrumb/step state)
    dispatch(clearInventory());
    dispatch(fetchInventoryCountries());
    dispatch(fetchCart());
  }, [dispatch]);

  // ── Core search (used when no specific city is selected) ──────────────────
  const runSearch = useCallback((country, activeFilters, p) => {
    dispatch(searchInventoryNumbers({
      country_name_contains: country.name,
      ...activeFilters,
      page:     p,
      per_page: 25,
    }));
  }, [dispatch]);

  // ── City-specific fetch (exact city ID — no accent/substring issues) ───────
  // Checks an in-memory session cache first so switching between cities is instant.
  const runCityFetch = useCallback((countryId, cityId, p) => {
    const key = `${countryId}_${cityId}_${p}`;
    if (cityCache.current[key]) {
      // Cache hit → dispatch synchronously, no API call
      dispatch(loadFromCache(cityCache.current[key]));
      return;
    }
    // Cache miss → fetch from API and store result
    dispatch(fetchNumbersByCity({
      countryId,
      cityId,
      params: { page: p, per_page: 25 },
    })).then((action) => {
      if (!action.error && action.payload) {
        cityCache.current[key] = action.payload;
      }
    });
  }, [dispatch]);

  // ── Country click ─────────────────────────────────────────────────────────
  const handleCountrySelect = (c) => {
    dispatch(setSelectedCountry(c));
    const initial = { number_contains: "", city_name_contains: "" };
    setFilters(initial);
    setPage(1);
    setActiveCityId(null);
    cityCache.current = {}; // each country gets a fresh cache
    dispatch(fetchCities({ countryId: c.id })); // for city dropdown
    runSearch(c, initial, 1);
    setStep(2);
  };

  // ── City selection — uses numeric city ID → GET /cities/:id/dids (exact) ──
  const handleCitySelect = (cityId, cityName) => {
    if (cityId) {
      setFilters((prev) => ({ ...prev, city_name_contains: cityName }));
      setActiveCityId(cityId);
      setPage(1);
      runCityFetch(selectedCountry.id, cityId, 1);
    } else {
      // City cleared — go back to full-country search
      setFilters((prev) => ({ ...prev, city_name_contains: "" }));
      setActiveCityId(null);
      setPage(1);
      runSearch(selectedCountry, { ...filters, city_name_contains: "" }, 1);
    }
  };

  // ── Other filter changes (number search + clear-all) ──────────────────────
  const handleFilterChange = (key, value) => {
    clearTimeout(debounceRef.current);
    const next = { ...filters, [key]: value };
    setFilters(next);

    // Clearing city via "Clear all" or the city tag ×
    if (key === "city_name_contains" && !value) {
      setActiveCityId(null);
      setPage(1);
      runSearch(selectedCountry, next, 1);
      return;
    }

    // Number search (debounced)
    if (key === "number_contains") {
      setActiveCityId(null);
      debounceRef.current = setTimeout(() => {
        setPage(1);
        runSearch(selectedCountry, next, 1);
      }, 500);
    }
  };

  const handlePageChange = (p) => {
    setPage(p);
    if (activeCityId) {
      runCityFetch(selectedCountry.id, activeCityId, p);
    } else {
      runSearch(selectedCountry, filters, p);
    }
  };

  const handleBackToCountries = () => {
    dispatch(clearInventory());
    clearTimeout(debounceRef.current);
    setFilters({ number_contains: "", city_name_contains: "" });
    setActiveCityId(null);
    setPage(1);
    setStep(1);
    cityCache.current = {}; // clear cache when leaving country
  };

  // ── Cart actions ──────────────────────────────────────────────────────────
  const handleAddToCart = (did) => {
    dispatch(addToCart({
      did_id:             did.id,
      number:             did.number,
      country:            did.country || selectedCountry?.name || "",
      country_short_name: did.country_short_name || selectedCountry?.short_name || "",
      city:               did.city || "",
      channels:           did.channels || 1,
      activation:         did.activation || 0,
      monthly_fee:        did.monthly_fee || did.monthly || 0,
      per_minute:         did.per_minute || 0,
      ourActivationPrice: did.ourActivationPrice || 0,
      ourMonthlyPrice:    did.ourMonthlyPrice || 0,
      ourPerMinute:       did.ourPerMinute || 0,
      required_documents: did.required_documents || [],
    }));
  };

  const handleRemoveFromCart = (did_id) => {
    dispatch(removeFromCart(did_id));
  };

  const breadcrumbs = ["Countries"];
  if (selectedCountry) breadcrumbs.push(selectedCountry.name);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card" style={{ padding: "20px 24px", borderRadius: 12 }}>

        {/* Page header */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20, flexWrap: "wrap", gap: 12,
        }}>
          <h4 style={{ fontWeight: 700, marginBottom: 0 }}>
            <i className="ti ti-phone-plus" style={{ marginRight: 8, color: "#667eea" }} />
            Browse Numbers
          </h4>
        </div>

        {/* Breadcrumb */}
        {breadcrumbs.length > 1 && (
          <Breadcrumb
            steps={breadcrumbs}
            onClickIndex={(i) => i === 0 && handleBackToCountries()}
            countryCode={selectedCountry?.short_name}
          />
        )}

        {/* Step 1 — Country grid */}
        {step === 1 && (
          <CountryStep
            countries={countries}
            loading={countriesLoading}
            onSelect={handleCountrySelect}
            cartItems={cartItems}
            onGoToCart={() => navigate(route.didNumberCart)}
          />
        )}

        {/* Step 2 — Results (single loading state on table only) */}
        {step === 2 && selectedCountry && (
          <SearchResultsStep
            country={selectedCountry}
            numbers={numbers}
            total={numbersTotal}
            loading={numbersLoading}
            page={page}
            cityOptions={cities || []}
            citiesLoading={citiesLoading}
            filters={filters}
            activeCityId={activeCityId}
            onFilterChange={handleFilterChange}
            onCitySelect={handleCitySelect}
            onPageChange={handlePageChange}
            cartItems={cartItems}
            cartAdding={cartAdding}
            cartRemoving={cartRemoving}
            onAddToCart={handleAddToCart}
            onRemoveFromCart={handleRemoveFromCart}
            onGoToCart={() => navigate(route.didNumberCart)}
            countries={countries}
            onCountryChange={handleCountrySelect}
          />
        )}

        </div>{/* end card */}
      </div>
    </div>
  );
};

export default BrowseNumbers;
